import { useEffect, useState } from "react";
import { ChevronRight, HelpCircle, LockKeyhole, Pencil, ShieldCheck, UserCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { logoutUser } from "../auth";
import UserLayout from "../components/Userlayout";

const fieldClass = "w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-blue-100";

export default function Profile() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({ name: "", email: "", workshopName: "", mobile: "" });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/auth/me"); setProfile(data?.user || data || {}); }
      catch { setMessage("Could not load your profile."); }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async (e) => {
    e.preventDefault(); setSaving(true); setMessage("");
    try {
      const { data } = await api.patch("/users/profile", profile);
      const updated = data.user || profile;
      setProfile(updated);
      const old = JSON.parse(localStorage.getItem("user") || "{}");
      localStorage.setItem("user", JSON.stringify({ ...old, ...updated }));
      setMessage("Profile updated successfully.");
      setEditing(false);
    } catch (err) { setMessage(err.response?.data?.message || "Could not update profile."); }
    finally { setSaving(false); }
  };

  const rows = [
    { icon: UserCircle2, title: "My Account", text: "Update your personal information", bg: "#eee9ff", color: "#3724a8", action: () => setEditing(true) },
    { icon: LockKeyhole, title: "Face ID / Touch ID", text: "Manage your device security", bg: "#e9f8ef", color: "#09ae50" },
    { icon: ShieldCheck, title: "Two-Factor Authentication", text: "Add an extra layer of security", bg: "#e6efff", color: "#2670ff" },
    { icon: HelpCircle, title: "Help & Support", text: "Get help and contact support", bg: "#fff0e6", color: "#ff6d00" },
    { icon: UserCircle2, title: "About App", text: "Version, terms and policies", bg: "#e9f8ef", color: "#3429a8" },
  ];

  return (
    <UserLayout>
      <main className="min-h-[calc(100vh-92px)] bg-[#f7f7f9] py-[76px]">
        <div className="mx-auto w-[min(1280px,calc(100%-36px))]">
          <section className="relative overflow-hidden rounded-t-[18px] bg-gradient-to-r from-[#2c24a8] via-[#2723a3] to-[#181273] px-[55px] py-[36px] text-white">
            <div className="flex items-center gap-9">
              <div className="grid h-[110px] w-[110px] place-items-center rounded-full bg-gradient-to-br from-[#f1d6bc] to-[#795342] text-[36px] font-bold text-white">{(profile.name?.[0] || "?").toUpperCase()}</div>
              <div><h1 className="text-[34px] font-bold">{profile.name || "Your name"}</h1><p className="mt-2 text-[14px]">{profile.mobile || "No mobile number added"}</p></div>
            </div>
            <button onClick={() => setEditing(true)} className="absolute right-[60px] top-[62px] grid h-[66px] w-[66px] place-items-center rounded-full bg-[#5548cf] text-white"><Pencil size={27}/></button>
            <div className="pointer-events-none absolute -right-14 -bottom-16 h-52 w-52 rounded-full border border-indigo-300/30" />
            <div className="pointer-events-none absolute -right-8 -bottom-10 h-40 w-40 rounded-full border border-indigo-300/30" />
          </section>

          <section className="bg-white px-[30px] py-1">
            {rows.map(({ icon: Icon, title, text, bg, color, action }) => (
              <button key={title} onClick={action} className="flex w-full items-center gap-10 border-b border-[#f2f2f4] px-0 py-[18px] text-left last:border-b-0">
                <span className="grid h-[68px] w-[68px] place-items-center rounded-[16px]" style={{ background:bg, color }}><Icon size={33} strokeWidth={1.8}/></span>
                <span className="flex-1"><strong className="text-[19px] text-[#172238]">{title}</strong><span className="mt-2 block text-[13px] text-[#8a8f98]">{text}</span></span>
                <ChevronRight size={25} className="mr-4 text-[#111b2a]"/>
              </button>
            ))}
          </section>

          <button onClick={async () => { await logoutUser(); navigate("/", { replace:true }); }} className="mx-auto mt-[36px] block h-[48px] w-[72%] rounded-[8px] bg-[#137ce5] text-[14px] text-white">Logout</button>
          {message && <p className="mt-4 text-center text-sm text-slate-600">{message}</p>}
        </div>

        {editing && (
          <div className="agp-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) setEditing(false); }}>
            <form onSubmit={save} className="w-[min(620px,94vw)] rounded-2xl bg-white p-7 shadow-2xl">
              <div className="mb-6 flex items-center justify-between"><div><h2 className="text-2xl font-bold text-slate-900">My Account</h2><p className="mt-1 text-sm text-slate-500">Update your personal information.</p></div><button type="button" onClick={() => setEditing(false)} className="rounded-lg border px-3 py-2 text-sm">Close</button></div>
              {loading ? <p className="py-8 text-center text-slate-400">Loading…</p> : <div className="grid gap-4 sm:grid-cols-2">{[["name","Full name"],["email","Email address"],["workshopName","Workshop name"],["mobile","Mobile number"]].map(([key,label]) => <label key={key} className="text-sm font-medium text-slate-700">{label}<input className={`${fieldClass} mt-1`} value={profile[key] || ""} onChange={(e) => setProfile((p) => ({ ...p, [key]:e.target.value }))}/></label>)}</div>}
              <button disabled={saving} className="mt-6 h-11 w-full rounded-lg bg-[#137ce5] font-semibold text-white disabled:opacity-50">{saving ? "Saving…" : "Save Changes"}</button>
            </form>
          </div>
        )}
      </main>
    </UserLayout>
  );
}
